import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'
import { Plus, Edit, Trash2, Eye, Star, Loader2, CheckCircle2, Search, X, Image as ImageIcon } from 'lucide-react'

const AdminProgrammes = () => {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatut, setSelectedStatut] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentProgramme, setCurrentProgramme] = useState(null)
  const [activeTab, setActiveTab] = useState('general')
  const [isUploading, setIsUploading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-programmes', selectedStatut],
    queryFn: async () => {
      const res = await api.get('/programmes/', { 
        params: { 
          limit: 100,
          statut: selectedStatut || undefined
        } 
      })
      return res.data
    }
  })

  const saveMutation = useMutation({
    mutationFn: (programme) => {
      const { id, created_at, updated_at, articles, medias, galerie_items, ...cleanData } = programme
      
      const payload = {
        ...cleanData,
        date_debut: cleanData.date_debut || null,
        date_fin: cleanData.date_fin || null,
        beneficiaires: parseInt(cleanData.beneficiaires) || 0,
        epingle_accueil: !!cleanData.epingle_accueil
      }

      if (id) {
        return api.put(`/programmes/${id}`, payload)
      } else {
        const slugBase = payload.titre.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
        const slug = payload.slug || `${slugBase}-${Math.random().toString(36).substring(2, 7)}`
        return api.post('/programmes/', { ...payload, slug })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-programmes'])
      setIsModalOpen(false)
      setCurrentProgramme(null)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/programmes/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-programmes'])
  })

  const toggleFeaturedMutation = useMutation({
    mutationFn: ({ id, epingle }) => api.put(`/programmes/${id}`, { epingle_accueil: !epingle }),
    onSuccess: () => queryClient.invalidateQueries(['admin-programmes'])
  })

  const handleEdit = (programme) => {
    try {
      setCurrentProgramme({
        ...programme,
        date_debut: (typeof programme.date_debut === 'string') ? programme.date_debut.split('T')[0] : (programme.date_debut || ''),
        date_fin: (typeof programme.date_fin === 'string') ? programme.date_fin.split('T')[0] : (programme.date_fin || '')
      })
      setActiveTab('general')
      setIsModalOpen(true)
    } catch (err) {
      console.error("Error opening edit modal:", err)
      // Fallback
      setCurrentProgramme(programme)
      setIsModalOpen(true)
    }
  }

  const handleCreate = () => {
    setCurrentProgramme({
      titre: '',
      categorie: 'Finance & Comptabilité',
      description_courte: '',
      contenu_riche: '',
      image_couverture: '',
      date_debut: '',
      date_fin: '',
      lieu: '',
      partenaire: '',
      beneficiaires: 0,
      statut: 'brouillon',
      epingle_accueil: false
    })
    setActiveTab('general')
    setIsModalOpen(true)
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setIsUploading(true)
    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await api.post('/medias/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      setCurrentProgramme({ ...currentProgramme, image_couverture: res.data.chemin })
    } catch (error) {
      console.error("Upload failed", error)
      alert("Échec de l'upload de l'image")
    } finally {
      setIsUploading(false)
    }
  }

  const filteredProgrammes = (data?.items || []).filter(item => 
    item.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.categorie.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cidh-gold" size={48} /></div>

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-cidh-blue font-montserrat tracking-tight">Gestion des Programmes</h3>
          <p className="text-gray-500 text-sm">Gérez les modules de formations professionnelles</p>
        </div>
        <button 
          onClick={handleCreate}
          className="btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all hover:scale-105 active:scale-95"
        >
          <Plus size={20} /> Nouveau Programme
        </button>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher par titre ou catégorie..." 
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <select 
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all bg-white text-sm"
            value={selectedStatut}
            onChange={(e) => setSelectedStatut(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="brouillon">Brouillon</option>
            <option value="publié">Publié</option>
            <option value="archivé">Archivé</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-cidh-gray-light text-gray-500 text-xs uppercase font-bold">
              <tr>
                <th className="px-6 py-4">Image</th>
                <th className="px-6 py-4">Titre</th>
                <th className="px-6 py-4 text-center">Statut</th>
                <th className="px-6 py-4 text-center">Épinglé</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredProgrammes?.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                      <img 
                        src={entry.image_couverture ? `/media/${entry.image_couverture}` : 'https://placehold.co/48x48?text=Formation'} 
                        className="w-full h-full object-cover"
                        alt={entry.titre}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-cidh-blue text-sm">{entry.titre}</div>
                    <div className="text-xs text-gray-400 capitalize">{entry.categorie}</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                      entry.statut === 'publié' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {entry.statut}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button 
                      onClick={() => toggleFeaturedMutation.mutate({ id: entry.id, epingle: entry.epingle_accueil })}
                      className={entry.epingle_accueil ? 'text-cidh-gold' : 'text-gray-300'}
                    >
                      <Star fill={entry.epingle_accueil ? 'currentColor' : 'none'} size={20} className="mx-auto" />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <a 
                        href={`/programmes/${entry.slug}`} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye size={18} />
                      </a>
                      <button 
                        onClick={() => handleEdit(entry)}
                        className="p-2 text-cidh-gold hover:bg-gold-50 rounded-lg transition-colors"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        onClick={() => window.confirm('Supprimer ce programme ?') && deleteMutation.mutate(entry.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div style={{ opacity: 1, transform: 'scale(1)', visibility: 'visible' }} className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl relative">
            <div className="p-6 border-b flex justify-between items-center bg-white sticky top-0 z-10">
              <h4 className="text-xl font-bold text-cidh-blue font-montserrat">
                {currentProgramme?.id ? 'Modifier le Programme' : 'Nouveau Programme'}
              </h4>
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                type="button"
              >
                <X size={24} />
              </button>
            </div>
            
              <div className="flex border-b bg-gray-50/50">
                <button 
                  type="button"
                  onClick={() => setActiveTab('general')}
                  className={`flex items-center gap-2 px-6 py-4 font-bold text-sm transition-all border-b-2 ${activeTab === 'general' ? 'border-cidh-gold text-cidh-blue bg-white' : 'border-transparent text-gray-500 hover:text-cidh-blue'}`}
                >
                  Général
                </button>
                <button 
                  type="button"
                  onClick={() => setActiveTab('details')}
                  className={`flex items-center gap-2 px-6 py-4 font-bold text-sm transition-all border-b-2 ${activeTab === 'details' ? 'border-cidh-gold text-cidh-blue bg-white' : 'border-transparent text-gray-500 hover:text-cidh-blue'}`}
                >
                  Médias
                </button>
              </div>

            <form id="programme-form" className="flex-grow overflow-y-auto p-8" onSubmit={(e) => {
              e.preventDefault()
              saveMutation.mutate(currentProgramme)
            }}>
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Titre du Programme *</label>
                    <input type="text" required className="input-field" value={currentProgramme?.titre || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, titre: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase">Catégorie</label>
                      <select className="input-field" value={currentProgramme?.categorie || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, categorie: e.target.value})}>
                        <option>Finance & Comptabilité</option>
                        <option>RH & Management</option>
                        <option>Marketing & Vente</option>
                        <option>Informatique & Bureau</option>
                        <option>Leadership & Stratégie</option>
                        <option>Gestion de Projets</option>
                        <option>Logistique & Achats</option>
                        <option>Droit & Fiscalité</option>
                        <option>Agriculture & Rural</option>
                        <option>Communication</option>
                        <option>Secrétariat & Archives</option>
                        <option>Transit & Douane</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase">Statut</label>
                      <select className="input-field" value={currentProgramme?.statut || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, statut: e.target.value})}>
                        <option value="brouillon">Brouillon</option>
                        <option value="publié">Publié</option>
                        <option value="archivé">Archivé</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Description Courte</label>
                    <textarea rows="3" className="input-field resize-none" value={currentProgramme?.description_courte || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, description_courte: e.target.value})}></textarea>
                  </div>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="space-y-6">
                  {/* Media Content */}
                  <div className="space-y-4">
                    <label className="text-xs font-bold text-gray-500 uppercase">Image de Couverture *</label>
                    <div className="flex items-center gap-4">
                      {currentProgramme?.image_couverture && (
                        <div className="w-24 h-24 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-gray-50">
                          <img 
                            src={`/media/${currentProgramme.image_couverture}`} 
                            className="w-full h-full object-cover" 
                            alt="Prévisualisation"
                          />
                        </div>
                      )}
                      <div className="flex-grow">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-gray-50 hover:border-cidh-gold/50 transition-all group">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            {isUploading ? (
                              <Loader2 className="animate-spin text-cidh-gold mb-2" size={24} />
                            ) : (
                              <ImageIcon className="text-gray-400 group-hover:text-cidh-gold mb-2" size={24} />
                            )}
                            <p className="text-xs text-gray-500 group-hover:text-cidh-gold"><span className="font-bold">Déposez l'image de couverture</span> ou cliquez</p>
                            <p className="text-[10px] text-gray-400 mt-1">PNG, JPG (Max. 5MB)</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="epingle" checked={currentProgramme?.epingle_accueil || false} onChange={(e) => setCurrentProgramme({...currentProgramme, epingle_accueil: e.target.checked})} className="w-5 h-5 accent-cidh-gold" />
                    <label htmlFor="epingle" className="text-sm font-medium text-gray-700">Épingler sur la page d'accueil</label>
                  </div>

                  <hr className="border-gray-100" />

                  <div className="hidden">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">Date Début</label>
                        <input type="date" className="input-field" value={currentProgramme?.date_debut || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, date_debut: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">Date Fin</label>
                        <input type="date" className="input-field" value={currentProgramme?.date_fin || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, date_fin: e.target.value})} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">Lieu</label>
                        <input type="text" className="input-field" value={currentProgramme?.lieu || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, lieu: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">Bénéficiaires</label>
                        <input type="number" className="input-field" value={currentProgramme?.beneficiaires || 0} onChange={(e) => setCurrentProgramme({...currentProgramme, beneficiaires: parseInt(e.target.value) || 0})} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase">Partenaire</label>
                      <input type="text" className="input-field" value={currentProgramme?.partenaire || ''} onChange={(e) => setCurrentProgramme({...currentProgramme, partenaire: e.target.value})} />
                    </div>
                  </div>
                  
                  <div className="p-8 border-2 border-dashed border-gray-100 rounded-2xl text-center text-gray-400 italic">
                    Les détails avancés sont masqués pour cette version.
                  </div>
                </div>
              )}
            </form>

            <div className="p-6 border-t flex justify-end gap-4 bg-white sticky bottom-0 z-10">
              <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-400 hover:bg-gray-50 transition-all">Annuler</button>
              <button 
                form="programme-form"
                type="submit"
                disabled={saveMutation.isPending || isUploading}
                className="btn-primary flex items-center gap-2 py-3 px-10 rounded-xl transition-all active:scale-95 disabled:opacity-50"
              >
                {saveMutation.isPending || isUploading ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Internal CSS for consistent fields */}
      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in {
          animation: scaleIn 0.2s ease-out forwards;
        }
        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #E5E7EB;
          border-radius: 0.75rem;
          background-color: white;
          transition: all 0.2s;
          font-size: 0.875rem;
        }
        .input-field:focus {
          outline: none;
          border-color: #D4AF37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }
      `}</style>
    </div>
  )
}

export default AdminProgrammes
